import React from 'react';
import { API_BASE, toUrl } from '../lib/config';

type DoctorOpt = { slug: string; name: string };
type Section = { id: string; title: string; items: string[] };
type MediaOpt = { id?: string; name: string; url: string };
type ProfileRow = {
  id: string;
  doctorSlug: string;
  doctorName: string;
  profileName: string;
  shortTitle?: string | null;
  highlightSurname?: string | null;
  isActive: boolean;
  updatedAt: string;
};

type FullProfile = {
  name: string;
  shortTitle?: string | null;
  highlightSurname?: string | null;
  heroBg?: string | null;
  portrait?: string | null;
  intro?: string | null;
  bioColumns: string[][];
  sections: Section[];
};

const DoctorProfilesYonetim: React.FC = () => {
  const DEFAULT_HERO = '/assets/images/recete3.jpg';

  const [doctors, setDoctors] = React.useState<DoctorOpt[]>([]);
  const [mediaOpts, setMediaOpts] = React.useState<MediaOpt[]>([]);
  const [items, setItems] = React.useState<ProfileRow[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [q, setQ] = React.useState('');

  // Create form
  const [cDoctorSlug, setCDocSlug] = React.useState('');
  const [cName, setCName] = React.useState('');
  const [cShort, setCShort] = React.useState('');
  const [cHighlight, setCHighlight] = React.useState('');
  const [cPortrait, setCPortrait] = React.useState('');
  const [cIntro, setCIntro] = React.useState('');
  const [cBioCol1, setCBioCol1] = React.useState(''); // line-separated
  const [cBioCol2, setCBioCol2] = React.useState(''); // line-separated
  // Fixed sections items (create)
  const [cWorkItems, setCWorkItems] = React.useState<string[]>([]); // calisma-geçmişi
  const [cAreasItems, setCAreasItems] = React.useState<string[]>([]); // calisma-alanlari
  const [cEduItems, setCEduItems] = React.useState<string[]>([]); // egitim
  const [cActive, setCActive] = React.useState(true);
  const [creating, setCreating] = React.useState(false);

  // Edit form
  const [editingId, setEditingId] = React.useState('');
  const [eDoctorSlug, setEDocSlug] = React.useState('');
  const [eName, setEName] = React.useState('');
  const [eShort, setEShort] = React.useState('');
  const [eHighlight, setEHighlight] = React.useState('');
  const [eHeroBg, setEHeroBg] = React.useState('');
  const [ePortrait, setEPortrait] = React.useState('');
  const [eIntro, setEIntro] = React.useState('');
  const [eBioCol1, setEBioCol1] = React.useState('');
  const [eBioCol2, setEBioCol2] = React.useState('');
  // Fixed sections items (edit)
  const [eWorkItems, setEWorkItems] = React.useState<string[]>([]);
  const [eAreasItems, setEAreasItems] = React.useState<string[]>([]);
  const [eEduItems, setEEduItems] = React.useState<string[]>([]);
  const [eActive, setEActive] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  // Delete
  const [confirmId, setConfirmId] = React.useState('');
  const [confirmLoading, setConfirmLoading] = React.useState(false);
  const [deletingId, setDeletingId] = React.useState('');
  // Advanced modal visibility
  const [advancedOpen, setAdvancedOpen] = React.useState(false);
  // Create form visibility
  const [createOpen, setCreateOpen] = React.useState(false);

  const fetchDoctors = React.useCallback(async () => {
    try {
      // Prefer admin list if available; else public all published
      let res = await fetch(toUrl('/api/doctors/admin'));
      if (!res.ok) res = await fetch(toUrl('/api/doctors?limit=100&offset=0'));
      if (!res.ok) return;
      const data = await res.json();
      const opts: DoctorOpt[] = (data || []).map((d: any) => ({ slug: d.slug, name: d.name }));
      setDoctors(opts);
    } catch { /* ignore */ }
  }, []);

  const fetchMedia = React.useCallback(async () => {
    try {
      const res = await fetch(toUrl('/api/media/admin'));
      if (!res.ok) return;
      const data = await res.json();
      const opts: MediaOpt[] = (data || []).map((m: any) => ({ id: m.id, name: m.name || m.filename || m.url, url: m.url }));
      setMediaOpts(opts);
    } catch { /* ignore */ }
  }, []);

  const fetchProfiles = React.useCallback(async () => {
    try {
      setLoading(true); setError('');
      const res = await fetch(toUrl('/api/doctor-profiles/admin'));
      if (!res.ok) throw new Error('Liste alınamadı');
      const data = await res.json();
      setItems(data || []);
    } catch (e: any) { setError(e.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setLoading(false); }
  }, []);

  React.useEffect(() => { fetchDoctors(); fetchMedia(); fetchProfiles(); }, [fetchDoctors, fetchMedia, fetchProfiles]);

  const filtered = React.useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return items;
    return items.filter(p =>
      (p.doctorName || '').toLowerCase().includes(term) ||
      (p.doctorSlug || '').toLowerCase().includes(term) ||
      (p.profileName || '').toLowerCase().includes(term) ||
      (p.shortTitle || '').toLowerCase().includes(term)
    );
  }, [items, q]);
  const currentRow = React.useMemo(() => items.find(i => i.id === editingId) || null, [items, editingId]);

  const resetCreate = () => {
    setCDocSlug(''); setCName(''); setCShort(''); setCHighlight(''); setCPortrait(''); setCIntro(''); setCBioCol1(''); setCBioCol2(''); setCWorkItems([]); setCAreasItems([]); setCEduItems([]); setCActive(true);
  };

  const onCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cDoctorSlug || !cName.trim()) { setError('Lütfen doktor ve profil adını girin'); return; }
    try {
      setCreating(true); setError('');
      const body: any = {
        doctorSlug: cDoctorSlug,
        name: cName.trim(),
        shortTitle: cShort.trim() || null,
        highlightSurname: cHighlight.trim() || null,
        heroBgUrl: DEFAULT_HERO,
        portraitUrl: cPortrait.trim() || null,
        intro: cIntro.trim() || null,
        bioColumns: [
          cBioCol1 ? cBioCol1.split('\n').map(s => s.trim()).filter(Boolean) : [],
          cBioCol2 ? cBioCol2.split('\n').map(s => s.trim()).filter(Boolean) : [],
        ],
        sections: [
          { id: 'calisma-geçmişi', title: 'Çalışma Geçmişi', items: cWorkItems.filter(Boolean) },
          { id: 'calisma-alanlari', title: 'Çalışma Alanları', items: cAreasItems.filter(Boolean) },
          { id: 'egitim', title: 'Eğitim', items: cEduItems.filter(Boolean) },
        ],
        isActive: !!cActive,
      };
      const res = await fetch(toUrl('/api/doctor-profiles/admin'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error('Oluşturma başarısız');
      resetCreate(); await fetchProfiles(); setCreateOpen(false);
    } catch (er: any) { setError(er.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setCreating(false); }
  };

  const openEdit = async (row: ProfileRow) => {
    setEditingId(row.id);
    setEDocSlug(row.doctorSlug);
    try {
      // Fetch full profile by slug to populate extended fields
      const res = await fetch(toUrl(`/api/doctor-profiles/${row.doctorSlug}`));
      const prof: FullProfile | null = res.ok ? await res.json() : null;
      setEName(prof?.name || row.profileName || '');
      setEShort(prof?.shortTitle || row.shortTitle || '');
      setEHighlight(prof?.highlightSurname || '');
      setEHeroBg(prof?.heroBg || '');
      setEPortrait(prof?.portrait || '');
      setEIntro(prof?.intro || '');
      const col1 = prof?.bioColumns?.[0] || [];
      const col2 = prof?.bioColumns?.[1] || [];
      setEBioCol1(col1.join('\n'));
      setEBioCol2(col2.join('\n'));
      const secs = prof?.sections || [];
      setEWorkItems((secs.find(s => s.id === 'calisma-geçmişi')?.items) || []);
      setEAreasItems((secs.find(s => s.id === 'calisma-alanlari')?.items) || []);
      setEEduItems((secs.find(s => s.id === 'egitim')?.items) || []);
      setEActive(row.isActive);
    } catch {
      // fallback minimal
      setEName(row.profileName || '');
      setEShort(row.shortTitle || '');
      setEActive(row.isActive);
    }
  };

  const cancelEdit = () => {
    setEditingId(''); setEDocSlug(''); setEName(''); setEShort(''); setEHighlight(''); setEHeroBg(''); setEPortrait(''); setEIntro(''); setEBioCol1(''); setEBioCol2(''); setEWorkItems([]); setEAreasItems([]); setEEduItems([]); setEActive(true);
  };

  const onSave = async (id: string, row: ProfileRow) => {
    if (!eName.trim()) { setError('Lütfen profil adı girin'); return; }
    try {
      setSaving(true); setError('');
      const body: any = {
        doctorSlug: eDoctorSlug || row.doctorSlug,
        name: eName.trim(),
        shortTitle: eShort.trim() || null,
        highlightSurname: eHighlight.trim() || null,
        portraitUrl: ePortrait.trim() || null,
        intro: eIntro.trim() || null,
        bioColumns: [
          eBioCol1 ? eBioCol1.split('\n').map(s => s.trim()).filter(Boolean) : [],
          eBioCol2 ? eBioCol2.split('\n').map(s => s.trim()).filter(Boolean) : [],
        ],
        sections: [
          { id: 'calisma-geçmişi', title: 'Çalışma Geçmişi', items: eWorkItems.filter(Boolean) },
          { id: 'calisma-alanlari', title: 'Çalışma Alanları', items: eAreasItems.filter(Boolean) },
          { id: 'egitim', title: 'Eğitim', items: eEduItems.filter(Boolean) },
        ],
        isActive: !!eActive,
      };
      const res = await fetch(toUrl(`/api/doctor-profiles/admin/${id}`), { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error('Güncelleme başarısız');
      cancelEdit(); await fetchProfiles();
    } catch (er: any) { setError(er.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setSaving(false); }
  };

  const onDelete = (id: string) => setConfirmId(id);
  const confirmDelete = async () => {
    if (!confirmId) return;
    try {
      setConfirmLoading(true); setDeletingId(confirmId);
      const res = await fetch(toUrl(`/api/doctor-profiles/admin/${confirmId}`), { method: 'DELETE' });
      if (!res.ok) throw new Error('Silme başarısız');
      setConfirmId(''); await fetchProfiles();
    } catch (er: any) { setError(er.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setConfirmLoading(false); setDeletingId(''); }
  };

  return (
    <div className="admin-page" style={{ padding: 12 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>Doktor Profilleri</h2>
        <div style={{ fontSize: 12, color: '#6b7280' }}>Toplam: {items.length}</div>
      </div>

      {/* Create form */}
      {createOpen && (
      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>Yeni Profil Ekle</h3>
          <button type="button" onClick={() => { setCreateOpen(false); }} aria-label="Kapat" style={{ background: 'transparent', border: 0, fontSize: 18, lineHeight: 1, cursor: 'pointer', color: '#6b7280' }}>×</button>
        </div>
        <form onSubmit={onCreate} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Doktor</label>
            <select value={cDoctorSlug} onChange={(e) => setCDocSlug(e.target.value)} className="input">
              <option value="">Seçiniz</option>
              {doctors.map(d => <option key={d.slug} value={d.slug}>{d.name} ({d.slug})</option>)}
            </select>
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Profil Adı</label>
            <input type="text" value={cName} onChange={(e) => setCName(e.target.value)} className="input" required />
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Kısa Ünvan (ops.)</label>
            <input type="text" value={cShort} onChange={(e) => setCShort(e.target.value)} className="input" />
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Soyadı Vurgusu (ops.)</label>
            <input type="text" value={cHighlight} onChange={(e) => setCHighlight(e.target.value)} className="input" />
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Portre (Medya)</label>
            <select value={cPortrait} onChange={(e) => setCPortrait(e.target.value)} className="input">
              <option value="">Seçiniz</option>
              {mediaOpts.map((m) => (
                <option key={m.url} value={m.url}>{m.name}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'grid', gap: 6, gridColumn: '1 / -1' }}>
            <label className="form-label">Kısa Tanıtım (intro)</label>
            <textarea value={cIntro} onChange={(e) => setCIntro(e.target.value)} className="input" rows={3} />
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Bio Kolon 1 (her satır bir madde)</label>
            <textarea value={cBioCol1} onChange={(e) => setCBioCol1(e.target.value)} className="input" rows={5} />
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Bio Kolon 2 (her satır bir madde)</label>
            <textarea value={cBioCol2} onChange={(e) => setCBioCol2(e.target.value)} className="input" rows={5} />
          </div>
          <div style={{ display: 'grid', gap: 6, gridColumn: '1 / -1' }}>
            <label className="form-label">Bölümler (sections)</label>
            <div style={{ display: 'grid', gap: 12 }}>
              {/* Çalışma Geçmişi */}
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>Çalışma Geçmişi</div>
                <div style={{ display: 'grid', gap: 8 }}>
                  {cWorkItems.map((it, idx) => (
                    <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8 }}>
                      <input className="input" type="text" value={it} onChange={(e) => { const arr = [...cWorkItems]; arr[idx] = e.target.value; setCWorkItems(arr); }} />
                      <button type="button" onClick={() => { const arr = [...cWorkItems]; arr.splice(idx,1); setCWorkItems(arr); }} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff' }}>Sil</button>
                    </div>
                  ))}
                  <div>
                    <button type="button" onClick={() => setCWorkItems([...cWorkItems, ''])} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #2563eb', background: '#fff', color: '#2563eb' }}>Madde Ekle</button>
                  </div>
                </div>
              </div>
              {/* Çalışma Alanları */}
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>Çalışma Alanları</div>
                <div style={{ display: 'grid', gap: 8 }}>
                  {cAreasItems.map((it, idx) => (
                    <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8 }}>
                      <input className="input" type="text" value={it} onChange={(e) => { const arr = [...cAreasItems]; arr[idx] = e.target.value; setCAreasItems(arr); }} />
                      <button type="button" onClick={() => { const arr = [...cAreasItems]; arr.splice(idx,1); setCAreasItems(arr); }} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff' }}>Sil</button>
                    </div>
                  ))}
                  <div>
                    <button type="button" onClick={() => setCAreasItems([...cAreasItems, ''])} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #2563eb', background: '#fff', color: '#2563eb' }}>Madde Ekle</button>
                  </div>
                </div>
              </div>
              {/* Eğitim */}
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>Eğitim</div>
                <div style={{ display: 'grid', gap: 8 }}>
                  {cEduItems.map((it, idx) => (
                    <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8 }}>
                      <input className="input" type="text" value={it} onChange={(e) => { const arr = [...cEduItems]; arr[idx] = e.target.value; setCEduItems(arr); }} />
                      <button type="button" onClick={() => { const arr = [...cEduItems]; arr.splice(idx,1); setCEduItems(arr); }} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff' }}>Sil</button>
                    </div>
                  ))}
                  <div>
                    <button type="button" onClick={() => setCEduItems([...cEduItems, ''])} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #2563eb', background: '#fff', color: '#2563eb' }}>Madde Ekle</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Aktif</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input id="cActive" type="checkbox" checked={cActive} onChange={(e) => setCActive(e.target.checked)} />
              <label htmlFor="cActive">Aktif</label>
            </div>
          </div>
          <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
            <button type="submit" disabled={creating} style={{ padding: '10px 16px', borderRadius: 8, border: '1px solid #16a34a', background: '#16a34a', color: '#fff', cursor: 'pointer', opacity: creating ? 0.8 : 1 }}>{creating ? 'Ekleniyor...' : 'Ekle'}</button>
          </div>
        </form>
        {cPortrait && (
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ fontSize: 12, color: '#6b7280' }}>Portre Önizleme:</div>
            <div style={{ width: 96, height: 96, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              <img
                src={toUrl(cPortrait)}
                alt="portrait"
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                crossOrigin="anonymous"
                onError={(e) => {
                  const el = e.currentTarget as HTMLImageElement;
                  const current = el.getAttribute('src') || '';
                  const raw = cPortrait;
                  const pref = toUrl(cPortrait);
                  const next = current === pref ? raw : pref;
                  if (current !== next) el.src = next;
                }}
              />
            </div>
          </div>
        )}
        {error && <div style={{ marginTop: 8, color: '#b00020' }}>{error}</div>}
      </div>
      )}

      {/* List & table */}
      <div className="card" style={{ padding: 0 }}>
        <div style={{ padding: 12, display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ara: doktor, slug, profil" className="input" style={{ width: 280 }} />
            {q && <button onClick={() => setQ('')} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>Temizle</button>}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={fetchProfiles} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>Yenile</button>
            <button onClick={() => { resetCreate(); setCreateOpen(true); }} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #16a34a', background: '#16a34a', color: '#fff', cursor: 'pointer' }}>Yeni Ekle</button>
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f3f4f6' }}>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Doktor</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Slug</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Profil Adı</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Kısa Ünvan</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Aktif</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Güncellenme</th>
                <th style={{ textAlign: 'right', padding: '12px 16px' }}>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={7} style={{ padding: 16, color: '#6b7280' }}>Yükleniyor...</td></tr>)}
              {!loading && filtered.length === 0 && (<tr><td colSpan={7} style={{ padding: 16, color: '#6b7280' }}>Kayıt bulunamadı.</td></tr>)}
              {filtered.map(row => (
                <tr key={row.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px 16px' }}>
                    {editingId === row.id ? (
                      <select value={eDoctorSlug || row.doctorSlug} onChange={(ev) => setEDocSlug(ev.target.value)} className="input">
                        {doctors.map(d => <option key={d.slug} value={d.slug}>{d.name} ({d.slug})</option>)}
                      </select>
                    ) : (
                      row.doctorName
                    )}
                  </td>
                  <td style={{ padding: '12px 16px' }}>{row.doctorSlug}</td>
                  <td style={{ padding: '12px 16px' }}>
                    {editingId === row.id ? (
                      <input type="text" value={eName} onChange={(e) => setEName(e.target.value)} className="input" />
                    ) : row.profileName}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {editingId === row.id ? (
                      <input type="text" value={eShort} onChange={(e) => setEShort(e.target.value)} className="input" />
                    ) : (row.shortTitle || '-')}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {editingId === row.id ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input id={`act-${row.id}`} type="checkbox" checked={eActive} onChange={(e) => setEActive(e.target.checked)} />
                        <label htmlFor={`act-${row.id}`}>Aktif</label>
                      </div>
                    ) : (
                      <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 12, background: row.isActive ? '#ecfdf5' : '#fef2f2', color: row.isActive ? '#065f46' : '#991b1b', border: `1px solid ${row.isActive ? '#a7f3d0' : '#fecaca'}` }}>{row.isActive ? 'Aktif' : 'Pasif'}</span>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px' }}>{row.updatedAt ? new Date(row.updatedAt).toLocaleString() : '-'}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                    {editingId === row.id ? (
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        <button onClick={() => setAdvancedOpen(true)} type="button" style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #2563eb', background: '#fff', color: '#2563eb', cursor: 'pointer' }}>Gelişmiş</button>
                        <button onClick={() => onSave(row.id, row)} disabled={saving} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #16a34a', background: '#16a34a', color: '#fff', cursor: 'pointer', opacity: saving ? 0.8 : 1 }}>{saving ? 'Kaydediliyor...' : 'Kaydet'}</button>
                        <button onClick={cancelEdit} disabled={saving} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>İptal</button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <button onClick={() => { openEdit(row); setAdvancedOpen(true); }} type="button" style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #2563eb', background: '#fff', color: '#2563eb', cursor: 'pointer' }}>Gelişmiş</button>
                        <button onClick={() => openEdit(row)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #2563eb', background: '#fff', color: '#2563eb', cursor: 'pointer' }}>Düzenle</button>
                        <button onClick={() => onDelete(row.id)} disabled={deletingId === row.id} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #dc2626', background: '#dc2626', color: '#fff', cursor: 'pointer', opacity: deletingId === row.id ? 0.8 : 1 }}>Sil</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Advanced Settings Modal */}
      {advancedOpen && editingId && (
        <div role="dialog" aria-modal="true" aria-labelledby="advTitle" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050 }} onClick={(e) => { if (e.target === e.currentTarget) setAdvancedOpen(false); }} onKeyDown={(e) => { if (e.key === 'Escape') setAdvancedOpen(false); }} tabIndex={-1}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(900px,96vw)', height: '90vh', maxHeight: '90vh', background: '#fff', borderRadius: 12, boxShadow: '0 10px 25px rgba(0,0,0,0.15)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h5 id="advTitle" style={{ margin: 0, fontSize: 18 }}>Gelişmiş Ayarlar</h5>
              <button type="button" aria-label="Close" onClick={() => setAdvancedOpen(false)} style={{ background: 'transparent', border: 0, fontSize: 18, lineHeight: 1, cursor: 'pointer', color: '#6b7280' }}>×</button>
            </div>
            <div style={{ padding: 16, overflowY: 'auto', flex: 1 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={{ display: 'grid', gap: 6 }}>
                  <label className="form-label">Portre (Medya)</label>
                  <select value={ePortrait} onChange={(ev) => setEPortrait(ev.target.value)} className="input">
                    <option value="">Seçiniz</option>
                    {mediaOpts.map((m) => (
                      <option key={m.url} value={m.url}>{m.name}</option>
                    ))}
                  </select>
                </div>
                <div style={{ display: 'grid', gap: 6 }}>
                  <label className="form-label">Soyadı Vurgusu (ops.)</label>
                  <input type="text" value={eHighlight} onChange={(e) => setEHighlight(e.target.value)} className="input" />
                </div>
                <div style={{ display: 'grid', gap: 6, gridColumn: '1 / -1' }}>
                  <label className="form-label">Kısa Tanıtım (intro)</label>
                  <textarea value={eIntro} onChange={(e) => setEIntro(e.target.value)} className="input" rows={4} />
                </div>
                <div style={{ display: 'grid', gap: 6 }}>
                  <label className="form-label">Bio Kolon 1 (satır başına bir madde)</label>
                  <textarea value={eBioCol1} onChange={(e) => setEBioCol1(e.target.value)} className="input" rows={5} />
                </div>
                <div style={{ display: 'grid', gap: 6 }}>
                  <label className="form-label">Bio Kolon 2 (satır başına bir madde)</label>
                  <textarea value={eBioCol2} onChange={(e) => setEBioCol2(e.target.value)} className="input" rows={5} />
                </div>
                <div style={{ display: 'grid', gap: 6, gridColumn: '1 / -1' }}>
                  <label className="form-label">Bölümler (sections)</label>
                  <div style={{ display: 'grid', gap: 12 }}>
                    <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
                      <div style={{ fontWeight: 600, marginBottom: 8 }}>Çalışma Geçmişi</div>
                      <div style={{ display: 'grid', gap: 8 }}>
                        {eWorkItems.map((it, idx) => (
                          <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8 }}>
                            <input className="input" type="text" value={it} onChange={(e) => { const arr = [...eWorkItems]; arr[idx] = e.target.value; setEWorkItems(arr); }} />
                            <button type="button" onClick={() => { const arr = [...eWorkItems]; arr.splice(idx,1); setEWorkItems(arr); }} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff' }}>Sil</button>
                          </div>
                        ))}
                        <div>
                          <button type="button" onClick={() => setEWorkItems([...eWorkItems, ''])} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #2563eb', background: '#fff', color: '#2563eb' }}>Madde Ekle</button>
                        </div>
                      </div>
                    </div>
                    <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
                      <div style={{ fontWeight: 600, marginBottom: 8 }}>Çalışma Alanları</div>
                      <div style={{ display: 'grid', gap: 8 }}>
                        {eAreasItems.map((it, idx) => (
                          <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8 }}>
                            <input className="input" type="text" value={it} onChange={(e) => { const arr = [...eAreasItems]; arr[idx] = e.target.value; setEAreasItems(arr); }} />
                            <button type="button" onClick={() => { const arr = [...eAreasItems]; arr.splice(idx,1); setEAreasItems(arr); }} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff' }}>Sil</button>
                          </div>
                        ))}
                        <div>
                          <button type="button" onClick={() => setEAreasItems([...eAreasItems, ''])} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #2563eb', background: '#fff', color: '#2563eb' }}>Madde Ekle</button>
                        </div>
                      </div>
                    </div>
                    <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
                      <div style={{ fontWeight: 600, marginBottom: 8 }}>Eğitim</div>
                      <div style={{ display: 'grid', gap: 8 }}>
                        {eEduItems.map((it, idx) => (
                          <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8 }}>
                            <input className="input" type="text" value={it} onChange={(e) => { const arr = [...eEduItems]; arr[idx] = e.target.value; setEEduItems(arr); }} />
                            <button type="button" onClick={() => { const arr = [...eEduItems]; arr.splice(idx,1); setEEduItems(arr); }} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff' }}>Sil</button>
                          </div>
                        ))}
                        <div>
                          <button type="button" onClick={() => setEEduItems([...eEduItems, ''])} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #2563eb', background: '#fff', color: '#2563eb' }}>Madde Ekle</button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div style={{ padding: 12, borderTop: '1px solid #e5e7eb', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setAdvancedOpen(false)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>Kapat</button>
              <button type="button" onClick={() => { if (currentRow) onSave(currentRow.id, currentRow); setAdvancedOpen(false); }} disabled={saving} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #16a34a', background: '#16a34a', color: '#fff', cursor: 'pointer', opacity: saving ? 0.8 : 1 }}>{saving ? 'Kaydediliyor...' : 'Kaydet'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {confirmId && (
        <div role="dialog" aria-modal="true" aria-labelledby="profDelTitle" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050 }} onClick={(e) => { if (e.target === e.currentTarget) setConfirmId(''); }} onKeyDown={(e) => { if (e.key === 'Escape') setConfirmId(''); }} tabIndex={-1}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(480px,92vw)', background: '#fff', borderRadius: 12, boxShadow: '0 10px 25px rgba(0,0,0,0.15)', overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h5 id="profDelTitle" style={{ margin: 0, fontSize: 18 }}>Silme Onayı</h5>
              <button type="button" aria-label="Close" onClick={() => setConfirmId('')} style={{ background: 'transparent', border: 0, fontSize: 18, lineHeight: 1, cursor: 'pointer', color: '#6b7280' }}>×</button>
            </div>
            <div style={{ padding: 16 }}>Bu doktor profilini silmek istediğinize emin misiniz? Bu işlem geri alınamaz.</div>
            <div style={{ padding: 12, borderTop: '1px solid #e5e7eb', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setConfirmId('')} disabled={confirmLoading} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>İptal</button>
              <button type="button" onClick={confirmDelete} disabled={confirmLoading} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #dc2626', background: '#dc2626', color: '#fff', cursor: 'pointer', opacity: confirmLoading ? 0.8 : 1 }}>{confirmLoading ? 'Siliniyor...' : 'Evet, Sil'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DoctorProfilesYonetim;
